<template>
  <router-view v-if="isMarketingShell" />
  <template v-else>
    <div class="app-shell" :class="{ 'app-shell--mobile-nav': isMobile }">
    <nav class="navbar navbar-expand-lg navbar-light bg-light app-top-nav">
      <div class="container-fluid ps-3">
        <AppBrand to="/" class="navbar-brand me-2" />
        <ul class="navbar-nav ms-auto align-items-center gap-1 flex-row app-top-nav__items">
          <li v-if="auth.isAuth && productAccessOk" class="nav-item">
            <router-link
              to="/search"
              class="btn nav-link app-nav-item border-0 text-dark"
              title="Поиск тендеров"
              aria-label="Поиск тендеров"
              active-class="text-primary"
            >
              <i class="fa-solid fa-magnifying-glass app-nav-icon" aria-hidden="true" />
              <span class="app-nav-label">Поиск</span>
            </router-link>
          </li>
          <li v-if="auth.isAuth && productAccessOk" class="nav-item">
            <router-link
              to="/lists"
              class="btn nav-link app-nav-item border-0 text-dark"
              title="Списки и канбан"
              aria-label="Списки и канбан"
              active-class="text-primary"
            >
              <i class="fa-solid fa-table-columns app-nav-icon" aria-hidden="true" />
              <span class="app-nav-label">Списки</span>
            </router-link>
          </li>
          <li v-if="auth.isAuth" class="nav-item">
            <button
              class="btn nav-link app-nav-item border-0 bg-transparent text-dark"
              type="button"
              title="Обратная связь"
              aria-label="Обратная связь"
              @click="showFeedback = true"
            >
              <i class="fa-solid fa-comment-dots app-nav-icon" aria-hidden="true" />
              <span class="app-nav-label">Обратная связь</span>
            </button>
          </li>
          <li v-if="auth.isAuth && auth.user?.is_staff" class="nav-item">
            <router-link
              to="/admin/dashboard"
              class="btn nav-link app-nav-item border-0 text-dark"
              title="Админка"
              aria-label="Админка"
              active-class="text-primary"
            >
              <i class="fa-solid fa-gauge-high app-nav-icon" aria-hidden="true" />
              <span class="app-nav-label">Админка</span>
            </router-link>
          </li>
          <li v-if="auth.isAuth" class="nav-item">
            <button
              class="btn nav-link app-nav-item border-0 bg-transparent text-dark"
              type="button"
              title="Кабинет"
              aria-label="Кабинет"
              @click="showCabinet = true"
            >
              <i class="fa-regular fa-user-circle app-nav-icon" aria-hidden="true" />
              <span class="app-nav-label">Кабинет</span>
            </button>
          </li>
          <li class="nav-item">
            <button
              class="btn nav-link app-nav-item border-0 bg-transparent text-dark"
              type="button"
              :title="auth.isAuth ? 'Выход' : 'Вход'"
              :aria-label="auth.isAuth ? 'Выход' : 'Вход'"
              @click="auth.isAuth ? auth.logout() : $router.push('/login')"
            >
              <i
                class="app-nav-icon"
                :class="auth.isAuth ? 'fa-solid fa-right-from-bracket' : 'fa-solid fa-right-to-bracket'"
                aria-hidden="true"
              />
              <span class="app-nav-label">{{ auth.isAuth ? 'Выход' : 'Вход' }}</span>
            </button>
          </li>
        </ul>
      </div>
    </nav>
    <main :class="mainClass">
      <router-view v-slot="{ Component }">
        <component :is="Component" @open-cabinet="showCabinet = true" />
      </router-view>
    </main>
    <AppBottomNav
      v-if="isMobile"
      :cabinet-active="showCabinet"
      @open-cabinet="showCabinet = true"
      @open-feedback="showFeedback = true"
    />
    </div>
    <TheModal :show="showCabinet" @close="showCabinet = false">
      <TheCabinet />
    </TheModal>
    <FeedbackModal :show="showFeedback" @close="showFeedback = false" />
    <SetupOnboardingModal
      :show="showSetupOnboarding"
      :user-id="setupUserId"
      :primary-key="setupPrimaryKey"
      :skip-criteria-step="setupSkipCriteriaStep"
      @close="closeSetupOnboarding"
      @skip="onSetupSkip"
      @manual="onSetupManual"
      @completed="onSetupCompleted"
      @integration-connected="onSetupIntegrationConnected"
    />
    <SalesContactFab v-if="auth.isAuth" @open-quiz="goLandingQuiz" />
  </template>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/store/pinia.js'
import { useUiStore } from '@/store/ui.js'
import api from '@/api'
import AppBrand from '@/components/AppBrand.vue'
import TheModal from '@/components/TheModal.vue'
import TheCabinet from '@/components/TheCabinet.vue'
import SetupOnboardingModal from '@/components/SetupOnboardingModal.vue'
import FeedbackModal from '@/components/FeedbackModal.vue'
import AppBottomNav from '@/components/layout/AppBottomNav.vue'
import SalesContactFab from '@/components/landing/SalesContactFab.vue'
import { useViewport } from '@/composables/useViewport.js'
import { useProductDensity } from '@/composables/useProductDensity.js'
import { checkNeedsSetup } from '@/utils/setupGate'
import { resolveProfileUserId } from '@/utils/resolveProfileUserId'
import { syncUserKeys, applyKeySyncToStorage } from '@/utils/keySync'
import { canUseProduct } from '@/utils/productAccess.js'
import { userShowsRetailBilling } from '@/utils/retailBilling'

const auth = useAuthStore()
const ui = useUiStore()
const { isMobile } = useViewport()
useProductDensity()
const route = useRoute()
const router = useRouter()
const showCabinet = ref(false)
const showFeedback = ref(false)
const showSetupOnboarding = ref(false)
const setupPrimaryKey = ref(null)
const setupSkipCriteriaStep = ref(false)
const setupCheckDoneForUser = ref(null)
const keysSyncDoneForUser = ref(null)

const setupUserId = computed(() => resolveProfileUserId(auth.user))

const isMarketingShell = computed(() => route.meta.marketingShell === true)
const productAccessOk = computed(() => canUseProduct(auth.user))
const mainClass = computed(() => {
  const base = route.meta.fullWidth ? 'lists-main container-fluid px-3 py-2' : 'container my-4'
  return isMobile.value ? `${base} app-shell--mobile-nav` : base
})

async function runKeysSync() {
  if (!auth.isAuth || isMarketingShell.value || !productAccessOk.value) return

  if (!auth.user) {
    try {
      await auth.fetchProfile()
    } catch {
      return
    }
  }

  const userId = setupUserId.value
  if (userId == null) return
  if (keysSyncDoneForUser.value === userId) return

  try {
    const syncResult = await syncUserKeys(api)
    keysSyncDoneForUser.value = userId
    applyKeySyncToStorage(userId, syncResult)
  } catch (e) {
    console.warn('[App] keys sync failed', e)
  }
}

async function runSetupCheck() {
  if (!auth.isAuth || isMarketingShell.value || !productAccessOk.value) return

  if (!auth.user) {
    try {
      await auth.fetchProfile()
    } catch {
      return
    }
  }

  await runKeysSync()

  if (auth.user?.is_staff) return

  const userId = setupUserId.value
  if (userId == null) return
  if (setupCheckDoneForUser.value === userId && !showSetupOnboarding.value) return

  try {
    const result = await checkNeedsSetup(api, { userId })
    setupCheckDoneForUser.value = userId
    if (!result.needsSetup) {
      showSetupOnboarding.value = false
      return
    }
    setupPrimaryKey.value = result.primaryKey
    setupSkipCriteriaStep.value = result.hasCriteria
    showSetupOnboarding.value = true
  } catch (e) {
    console.warn('[App] setup check failed', e)
  }
}

function closeSetupOnboarding() {
  showSetupOnboarding.value = false
}

function onSetupSkip() {
  showSetupOnboarding.value = false
}

function onSetupManual() {
  onSetupSkip()
  showCabinet.value = true
  if (productAccessOk.value) {
    ui.requestOpenKeyModal()
    if (route.path !== '/search') {
      router.push('/search')
    }
  }
}

function onSetupCompleted() {
  showSetupOnboarding.value = false
  setupCheckDoneForUser.value = setupUserId.value
}

async function onSetupIntegrationConnected() {
  keysSyncDoneForUser.value = null
  await runKeysSync()
  try {
    const result = await checkNeedsSetup(api, { userId: setupUserId.value, force: true })
    setupPrimaryKey.value = result.primaryKey
    setupSkipCriteriaStep.value = result.hasCriteria
  } catch (e) {
    console.warn('[App] setup refresh after integration', e)
  }
}

watch(
  () => auth.isAuth,
  (isAuth) => {
    if (!isAuth) {
      setupCheckDoneForUser.value = null
      keysSyncDoneForUser.value = null
      showSetupOnboarding.value = false
    }
  },
)

watch(
  () => [auth.isAuth, setupUserId.value, isMarketingShell.value, productAccessOk.value],
  () => {
    if (auth.isAuth && !isMarketingShell.value) runSetupCheck()
  },
)

function openCabinetIfBillingReturn() {
  if (!userShowsRetailBilling(auth.user)) return
  if (route.query?.billing === 'return') {
    showCabinet.value = true
  }
}

function goLandingQuiz() {
  router.push({ path: '/', query: { funnel: 'open' } })
}

watch(
  () => route.path,
  (path) => {
    if (path === '/account') showCabinet.value = true
    openCabinetIfBillingReturn()
  },
)

watch(
  () => route.query?.billing,
  () => openCabinetIfBillingReturn(),
  { immediate: true },
)

watch(
  () => ui.pendingOpenCabinetWinCriteria,
  (v) => {
    if (!v) return
    showCabinet.value = true
    ui.consumeOpenCabinetWinCriteria()
  },
)

onMounted(() => {
  if (auth.isAuth) {
    auth.fetchProfile().then(() => runSetupCheck()).catch(() => {})
  }
})
</script>

<style scoped>
:deep(.navbar-brand.app-brand) {
  align-self: center;
}

.app-nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 0.35rem 0.5rem !important;
  min-width: 3.25rem;
  line-height: 1.1;
}

.app-nav-icon {
  font-size: 1.15rem;
}

.app-nav-label {
  font-size: 0.65rem;
  font-weight: 500;
  white-space: nowrap;
}

@media (max-width: 991.98px) {
  .app-top-nav__items {
    display: none !important;
  }
}

@media (max-width: 575.98px) {
  .app-nav-label {
    display: none;
  }

  .app-nav-item {
    min-width: auto;
    padding: 0.5rem !important;
  }

  .app-nav-icon {
    font-size: 1.25rem;
  }
}
</style>
